import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  chakra,
  List,
  ListItem,
  Text,
} from '@chakra-ui/react';
import { useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { WarningIcon } from '~components/ui/icons';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';

export type CustomerDeletePreCheckResult = {
  blockingReasons: string[];
  deletableItems: string[];
};

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  customerName: string;
  preCheck: CustomerDeletePreCheckResult;
  onConfirm: () => void;
  onCancel: () => void;
}

const DELETABLE_ITEM_LABELS: Record<string, string> = {
  PLAYLISTS: 'playlists',
  POWER_SCHEDULES: 'powerSchedules',
  MULTICLAIM_CODES: 'multiClaimCodes',
  SITES: 'sites',
  CONFIGURATIONS: 'configurations',
  NOTIFICATION_SETTINGS: 'notificationSettings',
};

const BLOCKING_REASON_KEYS: Record<string, string> = {
  HAS_CLAIMED_DISPLAYS: 'deleteCustomerBlockedDisplays',
  HAS_ZTE_ENROLMENTS: 'deleteCustomerBlockedZte',
};

export function CustomerDeleteModal({
  isOpen,
  isLoading,
  customerName,
  preCheck,
  onConfirm,
  onCancel,
}: Props) {
  const cancelRef = useRef(null);
  const { t } = useTranslation();

  const isBlocked = preCheck.blockingReasons.length > 0;
  const hasMultipleDeletable = preCheck.deletableItems.length > 1;

  return (
    <AlertDialog leastDestructiveRef={cancelRef} onClose={onCancel} isOpen={isOpen}>
      <AlertDialogOverlay />
      <AlertDialogContent>
        <AlertDialogHeader>
          <Box display="flex" alignItems="center">
            <WarningIcon color="red.400" marginRight="4" />
            <chakra.span>{t('deleteCustomer')}</chakra.span>
          </Box>
        </AlertDialogHeader>
        <ModalCloseButton onClick={onCancel} />

        <AlertDialogBody>
          {isBlocked ? (
            <>
              {preCheck.blockingReasons.map((reason) => (
                <Text key={reason} mb="3">
                  {t(BLOCKING_REASON_KEYS[reason] ?? reason)}
                </Text>
              ))}
            </>
          ) : preCheck.deletableItems.length === 0 ? (
            <Text>
              <Trans
                i18nKey="deleteCustomerConfirm"
                values={{ name: customerName }}
                components={{ strong: <strong /> }}
              />
            </Text>
          ) : hasMultipleDeletable ? (
            <>
              <Text>
                <Trans
                  i18nKey="deleteCustomerConfirmMultiple"
                  values={{ name: customerName }}
                  components={{ strong: <strong /> }}
                />
              </Text>
              <List mt="3" spacing="1">
                {preCheck.deletableItems.map((item) => (
                  <ListItem key={item}>{t(DELETABLE_ITEM_LABELS[item] ?? item)}</ListItem>
                ))}
              </List>
            </>
          ) : (
            <Text>
              <Trans
                i18nKey={`deleteCustomerConfirm_${preCheck.deletableItems[0]}`}
                values={{ name: customerName }}
                components={{ strong: <strong /> }}
              />
            </Text>
          )}
        </AlertDialogBody>

        <AlertDialogFooter>
          <Button
            variant="ghost"
            colorScheme="gray"
            ref={cancelRef}
            onClick={onCancel}
            isDisabled={isLoading}
          >
            {t('cancel')}
          </Button>
          {!isBlocked && (
            <Button
              colorScheme="red"
              marginLeft="3"
              onClick={onConfirm}
              isLoading={isLoading}
              isDisabled={isLoading}
            >
              {t('delete')}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
